import { defineCopy } from '../pageCopy';
import { getProduct } from '../../products';

const product = getProduct('dilamenu')!;

const en = {
  seo: { title: product.seo.title, description: product.seo.description },
  hero: {
    title: 'digital menus, beautifully served.',
    lede: 'A QR menu guests open in two seconds, ordering that reaches your kitchen, and a POS built for Iraqi hospitality.',
    secondary: 'Open a live menu',
    liveLabel: product.liveLabel,
    imageAlt: product.imageAlt,
    welcomeAlt: 'dila.menu welcome screen offering English, Arabic and Kurdish.',
    itemAlt: 'dila.menu item detail showing weight, calories, origin and ingredients.',
  },
  stats: {
    aria: 'dila.menu in numbers',
    labels: ['Venues live', 'Menu themes', 'Menu page weight', 'First load'],
  },
  themes: {
    aria: 'Themes',
    title: 'eleven themes. one qr code.',
    lede: 'From white-tablecloth editorial to Kurdish kilim craft, each theme is a full architecture with its own typography, rhythm and motion. Three layouts, three densities, contrast enforced.',
    shotAlt: 'The dila.menu theme gallery showing eight distinct menu architectures.',
  },
  ordering: {
    aria: 'Ordering',
    title: "ordering without the middleman's cut",
    lede: 'Guests order from the table or from home. Dine-in orders land straight in the POS; pickup and delivery hand off to your WhatsApp. No marketplace commission, payments settle at your till.',
    rows: [
      {
        title: 'Reservations included',
        body: 'Three-step booking flows into the POS, where staff confirm, assign a table, or mark a no-show.',
      },
      {
        title: 'Call-waiter and feedback',
        body: 'The QR on the table is also the waiter button and the five-star review box.',
      },
    ],
    shotAlt:
      'dila RMS floor in Kurdish with live table cards, running bills in IQD and the cashier rail, receiving dine-in orders from the QR menu.',
    shotUrl: 'dila RMS · incoming orders',
  },
  pulse: {
    aria: 'Live analytics',
    shotAlt:
      'dila.menu LivePulse admin in Kurdish showing a live revenue goal ring, top items and real-time activity feed.',
    title: "know your night while it's happening",
    lede: "LivePulse rings your revenue goal as orders land, ranks tonight's top dishes and streams the activity feed. The whole admin runs on the phone in your pocket, in Kurdish, Arabic or English.",
    rows: [
      {
        title: 'Edit the menu from the floor',
        body: '86 a sold-out dish, drop a photo, reorder categories. The printed QR never changes, the menu always can.',
      },
      {
        title: 'Print-ready QR designer',
        body: 'Menu, order, table and Wi-Fi codes with captions and backgrounds, exported as bulk PDFs for the print shop.',
      },
    ],
  },
  beyond: {
    aria: 'Beyond restaurants',
    title: 'not just restaurants',
    lede: 'Barbershops, laundries and motels run on the same engine: service matrices, per-vertical wording, the same two-second QR.',
    barberAlt:
      'A barbershop running on dila.menu with a noir theme and priced service cards in IQD.',
    barberCaption: 'Swan Barber Shop · noir theme',
    laundryAlt:
      'A laundry running on dila.menu taking online orders with a garment-by-service price matrix, wash and iron priced separately in IQD.',
    laundryCaption: 'Swan Laundry · wash and iron matrix',
  },
  craft: {
    aria: 'Craft',
    text: 'a menu should feel like the space it lives in',
    tagline: 'Warm · clear · unmistakably yours',
  },
  rms: {
    aria: 'dila RMS',
    eyebrow: 'dila RMS · the companion POS',
    title: 'then the kitchen takes over',
    lede: 'dila.menu feeds the floor; dila RMS runs it. The restaurant management system the same venues close their nights on, for Windows, macOS, Android and web.',
    tablesAlt:
      'dila RMS table overview with open and seated tables, running bills in IQD and live revenue chips.',
    tablesUrl: 'dila RMS · tables',
    platformsAlt:
      'dila RMS delivery platform picker with Talabat, Lezzoo, Toters, phone order and walk-in.',
    platformsUrl: 'dila RMS · delivery',
    rows: [
      {
        title: 'Tables, courses and running bills',
        body: 'A live floor grid with per-table bills, VIP handling and a presence lock when two devices open the same table.',
      },
      {
        title: 'Kitchen, bar and hookah screens',
        body: 'Each station sees only its items, with new-order alarms and ready boards. Nothing prints where it should not.',
      },
      {
        title: 'Iraqi payments built in',
        body: 'Cash, card, FIB, Qi and ZainCash tenders, split payments, cashier sessions with variance, and on-credit customer ledgers.',
      },
      {
        title: 'Delivery without chaos',
        body: 'A live Talabat integration, Lezzoo and Toters boards, platform ref codes and payout reconciliation with short-pay protection.',
      },
      {
        title: 'Kurdish-first printing, offline-first core',
        body: 'Receipts print in Kurdish first, routed to the right printer, and the queue survives an internet outage mid-service.',
      },
    ],
  },
  clients: {
    aria: 'Clients',
    label: 'Running tonight, somewhere in Kurdistan',
  },
  demo: {
    title: 'put your menu in every pocket',
    sub: 'QRs printed, theme picked, menu translated, staff trained. Most venues go live in days, and your first menu can mirror the one on your tables tonight.',
    secondary: 'Open a live menu',
    liveNote: 'Live',
  },
};

type Copy = typeof en;

const ar: Copy = {
  seo: {
    title: 'dila.menu: منيو رقمي وإدارة مطاعم لكوردستان',
    description:
      'dila.menu من Datacode منصة المنيو الرقمي بكوردستان: منيو QR بثلاث لغات، وطلبات عبر WhatsApp، وحجوزات، وتحليلات مباشرة، مع dila RMS نظام إدارة المطاعم بخدمة الطاولات وشاشات المطبخ والمدفوعات العراقية وربط Talabat.',
  },
  hero: {
    title: 'منيو رقمي، يتقدم بأناقة.',
    lede: 'منيو QR يفتحه الضيف بثانيتين، وطلبات توصل لمطبخك، ونقطة بيع مصممة للضيافة العراقية.',
    secondary: 'افتح منيو مباشر',
    liveLabel: 'منيوهات مباشرة',
    imageAlt:
      'منيو مطعم من dila.menu بتصميم الكلِم يعرض طبق أضنة كباب مميز من الشيف، مع الأسعار بالدينار العراقي وصور طعام.',
    welcomeAlt: 'شاشة الترحيب بـ dila.menu تعرض اختيار الإنكليزية والعربية والكوردية.',
    itemAlt: 'تفاصيل صنف بـ dila.menu تعرض الوزن والسعرات والمنشأ والمكونات.',
  },
  stats: {
    aria: 'dila.menu بالأرقام',
    labels: ['مكان يشتغل', 'تصاميم المنيو', 'وزن صفحة المنيو', 'أول تحميل'],
  },
  themes: {
    aria: 'التصاميم',
    title: 'أحد عشر تصميماً. رمز QR واحد.',
    lede: 'من طابع المطاعم الراقية (السفرة البيضاء) لين حرفة الكلِم الكوردي، كل تصميم بنية كاملة بخطوطه وإيقاعه وحركته. ثلاث تخطيطات، وثلاث كثافات، والتباين مضمون.',
    shotAlt: 'معرض تصاميم dila.menu يعرض ثمان بنى مختلفة للمنيو.',
  },
  ordering: {
    aria: 'الطلبات',
    title: 'طلبات بدون عمولة الوسيط',
    lede: 'الضيوف يطلبون من الطاولة أو من البيت. طلبات الصالة توصل مباشرة لنقطة البيع، والاستلام والتوصيل ينتقلون لـ WhatsApp مالك. ماكو عمولة سوق، والدفع يتم عندك بالصندوق.',
    rows: [
      {
        title: 'الحجوزات مشمولة',
        body: 'حجز من ثلاث خطوات ينتقل لنقطة البيع، وهناك الموظفين يأكدون أو يخصصون طاولة أو يسجلون عدم حضور.',
      },
      {
        title: 'نداء النادل والتقييم',
        body: 'رمز QR اللي على الطاولة هو نفسه زر نداء النادل وصندوق تقييم الخمس نجوم.',
      },
    ],
    shotAlt:
      'صالة dila RMS بالكوردي مع بطاقات طاولات مباشرة وفواتير مفتوحة بالدينار العراقي وشريط الكاشير، تستقبل طلبات الصالة من منيو QR.',
    shotUrl: 'dila RMS · الطلبات الواردة',
  },
  pulse: {
    aria: 'التحليلات المباشرة',
    shotAlt:
      'لوحة إدارة LivePulse بـ dila.menu بالكوردي تعرض حلقة هدف الإيراد المباشرة وأكثر الأصناف مبيعاً وسجل النشاط لحظة بلحظة.',
    title: 'اعرف ليلتك وهي تصير',
    lede: 'LivePulse يعبّي حلقة هدف الإيراد كل ما توصل طلبات، ويرتب أكثر أطباق الليلة مبيعاً، ويعرض سجل النشاط لحظة بلحظة. لوحة الإدارة كلها تشتغل على الموبايل اللي بجيبك، بالكوردي أو العربي أو الإنكليزي.',
    rows: [
      {
        title: 'عدّل المنيو من أرض المطعم',
        body: 'أوقف طبقاً نفد، وضيف صورة، ورتّب الأقسام. رمز QR المطبوع ما يتغير أبداً، بس المنيو دائماً يتغير.',
      },
      {
        title: 'مصمم QR جاهز للطباعة',
        body: 'رموز المنيو والطلب والطاولة والـ Wi-Fi مع عناوين وخلفيات، تنصدّر ملفات PDF دفعة وحدة لمطبعتك.',
      },
    ],
  },
  beyond: {
    aria: 'أبعد من المطاعم',
    title: 'مو بس مطاعم',
    lede: 'صالونات الحلاقة والمغاسل والموتيلات تشتغل على نفس المحرك: جداول خدمات، ومصطلحات خاصة بكل مجال، ونفس QR اللي يفتح بثانيتين.',
    barberAlt:
      'صالون حلاقة يشتغل على dila.menu بتصميم داكن (noir) وبطاقات خدمات بأسعارها بالدينار العراقي.',
    barberCaption: 'Swan Barber Shop · التصميم الداكن (noir)',
    laundryAlt:
      'مغسلة تشتغل على dila.menu وتستقبل طلبات أونلاين مع جدول أسعار حسب القطعة والخدمة، والغسيل والكوي بسعرين منفصلين بالدينار العراقي.',
    laundryCaption: 'Swan Laundry · جدول الغسيل والكوي',
  },
  craft: {
    aria: 'الحرفة',
    text: 'المنيو لازم يحسسك بالمكان اللي يعيش بي',
    tagline: 'دافئ · واضح · هو إلك بلا شك',
  },
  rms: {
    aria: 'dila RMS',
    eyebrow: 'dila RMS · نقطة البيع المرافقة',
    title: 'وبعدها المطبخ يستلم',
    lede: 'dila.menu يزوّد الصالة بالطلبات، وdila RMS يديرها. نظام إدارة المطاعم اللي نفس الأماكن تقفل بيه لياليها، على Windows وmacOS وAndroid والويب.',
    tablesAlt:
      'نظرة عامة على طاولات dila RMS مع الطاولات المفتوحة والمشغولة، والفواتير المفتوحة بالدينار العراقي، وشرائح إيراد مباشرة.',
    tablesUrl: 'dila RMS · الطاولات',
    platformsAlt:
      'اختيار منصة التوصيل بـ dila RMS مع Talabat وLezzoo وToters والطلب الهاتفي والزبون الحاضر.',
    platformsUrl: 'dila RMS · التوصيل',
    rows: [
      {
        title: 'الطاولات والأطباق المتتابعة والفواتير المفتوحة',
        body: 'خريطة صالة مباشرة بفاتورة لكل طاولة، ومعاملة الـ VIP، وقفل حضور لما جهازين يفتحون نفس الطاولة.',
      },
      {
        title: 'شاشات المطبخ والبار والأركيلة',
        body: 'كل محطة تشوف أصنافها بس، مع تنبيهات الطلب الجديد ولوحات الجاهز. ماكو شي يطبع بمكان مو مكانه.',
      },
      {
        title: 'مدفوعات عراقية مدمجة',
        body: 'الدفع نقداً وبالبطاقة وFIB وQi وZainCash، وتقسيم الدفع، وجلسات كاشير مع فروقات الصندوق، ودفاتر ديون للزبائن.',
      },
      {
        title: 'توصيل بدون فوضى',
        body: 'ربط مباشر مع Talabat، ولوحات Lezzoo وToters، وأكواد مرجعية للمنصات، ومطابقة المدفوعات مع حماية من نقص الدفع.',
      },
      {
        title: 'طباعة كوردية أولاً، ونواة تشتغل بدون إنترنت',
        body: 'الوصولات تنطبع بالكوردي أولاً وتروح للطابعة الصحيحة، والطابور يبقى شغال حتى لو انقطع الإنترنت وسط الخدمة.',
      },
    ],
  },
  clients: {
    aria: 'العملاء',
    label: 'يشتغل الليلة، بمكان ما بكوردستان',
  },
  demo: {
    title: 'حط منيوك بكل جيب',
    sub: 'نطبع رموز QR، ونختار التصميم، ونترجم المنيو، وندرّب الموظفين. أغلب الأماكن تشتغل خلال أيام، ومنيوك الأول يكدر يطابق اللي على طاولاتك الليلة.',
    secondary: 'افتح منيو مباشر',
    liveNote: 'مباشر',
  },
};

const ckb: Copy = {
  seo: {
    title: 'dila.menu: مینیوی دیجیتاڵ و بەڕێوەبردنی ڕێستورانت بۆ کوردستان',
    description:
      'dila.menu لەلایەن Datacode پلاتفۆرمی مینیوی دیجیتاڵی کوردستانە: مینیوی QR بە سێ زمان، داواکاری لە ڕێگەی WhatsApp، حیجز و شیکاری زیندوو، لەگەڵ dila RMS سیستەمی بەڕێوەبردنی ڕێستورانت بە خزمەتی مێز، شاشەی چێشتخانە، پارەدانی عێراقی و یەکخستنی Talabat.',
  },
  hero: {
    title: 'مینیوی دیجیتاڵ، بە جوانی پێشکەشکراو.',
    lede: 'مینیوی QR کە میوان لە دوو چرکەدا دەیکاتەوە، داواکاری کە دەگاتە چێشتخانەکەت، و خاڵی فرۆشتنێک دروستکراو بۆ میوانداری عێراقی.',
    secondary: 'مینیوێکی زیندوو بکەرەوە',
    liveLabel: 'مینیوی زیندوو',
    imageAlt:
      'مینیوی ڕێستورانتێکی dila.menu بە ستایلی کلیم کە کەبابی ئەدەنەی تایبەتی شێف نیشان دەدات، لەگەڵ نرخ بە دینار و وێنەی خواردن.',
    welcomeAlt: 'شاشەی بەخێرهاتنی dila.menu کە هەڵبژاردنی ئینگلیزی، عەرەبی و کوردی پێشکەش دەکات.',
    itemAlt: 'وردەکاری خواردنێک لە dila.menu کە کێش، کالۆری، بنەڕەت و پێکهاتەکان نیشان دەدات.',
  },
  stats: {
    aria: 'dila.menu بە ژمارە',
    labels: ['شوێنی چالاک', 'ستایلی مینیو', 'قەبارەی لاپەڕەی مینیو', 'یەکەم بارکردن'],
  },
  themes: {
    aria: 'ستایلەکان',
    title: 'یازدە ستایل. یەک کۆدی QR.',
    lede: 'لە ستایلی ڕێستورانتی بەرز (مێزی بە سپی داپۆشراو) هەتا پیشەی کلیمی کوردی، هەر ستایلێک ئەندازەیەکی تەواوە بە تایپۆگرافی، ڕیتم و جووڵەی تایبەتی خۆی. سێ شێوازی دانان، سێ چڕی، و جیاوازی ڕەنگ دڵنیاکراوەتەوە.',
    shotAlt: 'گەلەری ستایلەکانی dila.menu کە هەشت ئەندازەی جیاوازی مینیو نیشان دەدات.',
  },
  ordering: {
    aria: 'داواکاری',
    title: 'داواکاری بێ بڕی ناوبژیوان',
    lede: 'میوانەکان لە سەر مێز یان لە ماڵەوە داوا دەکەن. داواکارییەکانی ناو هۆڵ ڕاستەوخۆ دەچنە خاڵی فرۆشتن، وەرگرتن و گەیاندن دەگوازرێنەوە بۆ هەژماری WhatsApp ی خۆت. هیچ کۆمیسیۆنێکی بازاڕ نییە و پارەدان لە سندوقی خۆت دەکرێت.',
    rows: [
      {
        title: 'حیجزکردن تێدایە',
        body: 'حیجزکردنی سێ هەنگاوی دەچێتە ناو خاڵی فرۆشتن، لەوێ کارمەندان پشتڕاستی دەکەنەوە، مێزێک دیاری دەکەن یان وەک نەهاتوو تۆماری دەکەن.',
      },
      {
        title: 'بانگکردنی گارسۆن و هەڵسەنگاندن',
        body: 'کۆدی QR ی سەر مێزەکە هەمان دوگمەی بانگکردنی گارسۆن و سندوقی هەڵسەنگاندنی پێنج ئەستێرەیە.',
      },
    ],
    shotAlt:
      'هۆڵی dila RMS بە کوردی لەگەڵ کارتی مێزی زیندوو، فاتورەی کراوە بە دینار و شریتی کاشێر، کە داواکارییەکانی ناو هۆڵ لە مینیوی QR وەردەگرێت.',
    shotUrl: 'dila RMS · داواکارییە هاتووەکان',
  },
  pulse: {
    aria: 'شیکاری زیندوو',
    shotAlt:
      'بەڕێوەبەرایەتی LivePulse لە dila.menu بە کوردی کە بازنەی ئامانجی داهاتی زیندوو، باشترین خواردنەکان و لیستی چالاکی بە کاتی ڕاستەقینە نیشان دەدات.',
    title: 'شەوەکەت بزانە لە کاتی ڕوودانیدا',
    lede: 'LivePulse بازنەی ئامانجی داهاتەکەت پڕ دەکاتەوە کاتێک داواکاری دێت، باشترین خواردنەکانی ئەمشەو ڕیز دەکات و چالاکییەکان بە زیندوویی نیشان دەدات. هەموو بەڕێوەبەرایەتییەکە لەسەر مۆبایلی ناو گیرفانت کار دەکات، بە کوردی، عەرەبی یان ئینگلیزی.',
    rows: [
      {
        title: 'مینیو لە ناو هۆڵەوە دەستکاری بکە',
        body: 'خواردنێکی تەواوبوو ڕابگرە، وێنەیەک دابنێ، پۆلەکان ڕێک بخەرەوە. کۆدی QR ی چاپکراو هەرگیز ناگۆڕێت، بەڵام مینیو هەمیشە دەتوانێت.',
      },
      {
        title: 'دیزاینەری QR ئامادە بۆ چاپ',
        body: 'کۆدی مینیو، داواکاری، مێز و Wi-Fi لەگەڵ ناونیشان و پاشبنەما، وەک PDFی کۆمەڵ هەناردە دەکرێن بۆ چاپخانە.',
      },
    ],
  },
  beyond: {
    aria: 'زیاتر لە ڕێستورانت',
    title: 'تەنها ڕێستورانت نییە',
    lede: 'سەلۆنی ڕیشتاشین، جلشووشتنگە و مۆتێل لەسەر هەمان مەکینە کار دەکەن: خشتەی خزمەتگوزاری، وشەی تایبەت بە هەر بوارێک، و هەمان QRی دوو چرکەیی.',
    barberAlt:
      'سەلۆنی ڕیشتاشینێک لەسەر dila.menu بە ستایلی noir و کارتی خزمەتگوزاری لەگەڵ نرخ بە دینار.',
    barberCaption: 'Swan Barber Shop · ستایلی noir',
    laundryAlt:
      'جلشووشتنگەیەک لەسەر dila.menu کە داواکاری ئۆنلاین وەردەگرێت لەگەڵ خشتەی نرخ بەپێی پارچە و خزمەتگوزاری، شووشتن و ئوتوو بە نرخی جیا بە دینار.',
    laundryCaption: 'Swan Laundry · خشتەی شووشتن و ئوتوو',
  },
  craft: {
    aria: 'پیشە',
    text: 'مینیو دەبێت هەستی ئەو شوێنە پێ بکرێت کە تێیدایە',
    tagline: 'گەرم · ڕوون · بێگومان هی تۆیە',
  },
  rms: {
    aria: 'dila RMS',
    eyebrow: 'dila RMS · خاڵی فرۆشتنی هاوڕێ',
    title: 'دواتر چێشتخانە وەریدەگرێت',
    lede: 'dila.menu داواکاری دەنێرێتە هۆڵەکە، dila RMS بەڕێوەی دەبات. سیستەمی بەڕێوەبردنی ڕێستورانت کە هەمان شوێنەکان شەوەکانیان پێ دادەخەن، بۆ Windows‏، macOS‏، Android و وێب.',
    tablesAlt:
      'ڕوانگەیەکی گشتی مێزەکانی dila RMS لەگەڵ مێزی کراوە و دانیشتوو، فاتورەی کراوە بە دینار و چیپی داهاتی زیندوو.',
    tablesUrl: 'dila RMS · مێزەکان',
    platformsAlt:
      'هەڵبژاردنی پلاتفۆرمی گەیاندنی dila RMS لەگەڵ Talabat‏، Lezzoo‏، Toters، داواکاری تەلەفۆنی و کڕیاری ئامادەبوو.',
    platformsUrl: 'dila RMS · گەیاندن',
    rows: [
      {
        title: 'مێز، خولی خواردن و فاتورەی کراوە',
        body: 'تۆڕی هۆڵی زیندوو بە فاتورەی هەر مێزێک، مامەڵەی VIP و قفڵی ئامادەبوون کاتێک دوو ئامێر هەمان مێز دەکەنەوە.',
      },
      {
        title: 'شاشەی چێشتخانە، بار و ئەرگیلە',
        body: 'هەر وێستگەیەک تەنها بەشی خۆی دەبینێت، لەگەڵ ئاگادارکردنەوەی داواکاری نوێ و تەختەی ئامادە. هیچ شتێک لە شوێنی هەڵە چاپ ناکرێت.',
      },
      {
        title: 'پارەدانی عێراقی تێدایە',
        body: 'پارەدان بە نەختینە، کارت، FIB‏، Qi و ZainCash، دابەشکردنی پارەدان، دانیشتنی کاشێر لەگەڵ جیاوازی، و دەفتەری قەرزی کڕیار.',
      },
      {
        title: 'گەیاندن بێ ئاڵۆزی',
        body: 'گرێدانی ڕاستەوخۆ لەگەڵ Talabat، تەختەی Lezzoo و Toters، کۆدی ڕیفرێنسی پلاتفۆرم و ڕێکخستنەوەی پارەدان لەگەڵ پاراستن لە کەمپێدان.',
      },
      {
        title: 'چاپی سەرەتا کوردی، ناوەکی ئۆفلاین',
        body: 'پسوولەکان سەرەتا بە کوردی چاپ دەکرێن و دەچنە چاپکەری دروست، و ڕیزەکە لە پچڕانی ئینتەرنێت لە ناوەڕاستی خزمەتگوزارییەکەدا دەمێنێتەوە.',
      },
    ],
  },
  clients: {
    aria: 'کڕیارەکان',
    label: 'ئەمشەو کار دەکات، لە شوێنێک لە کوردستان',
  },
  demo: {
    title: 'مینیوەکەت بخەرە هەموو گیرفانێک',
    sub: 'QR چاپ دەکرێت، ستایل هەڵدەبژێردرێت، مینیو وەردەگێڕدرێت و کارمەندان ڕا دەهێنرێن. زۆربەی شوێنەکان لە چەند ڕۆژێکدا دەست بە کار دەکەن، و یەکەم مینیوت دەتوانێت هەمان ئەو مینیوە بێت کە ئەمشەو لەسەر مێزەکانتە.',
    secondary: 'مینیوێکی زیندوو بکەرەوە',
    liveNote: 'زیندوو',
  },
};

export const dilaMenuCopy = defineCopy(en, ar, ckb);
